import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { JobDetail } from '../../components/jobs';
import { getJob, saveJob, unsaveJob, getSavedJobs } from '../../services/jobs';
import { useAuth } from '../../context/AuthContext';
import type { Job, SavedJob } from '../../types/job';

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, userType, userId } = useAuth();

  const [job, setJob] = useState<Job | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isApplied, setIsApplied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const loadJob = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getJob(id);
        setJob(data);

        // Check if expert has saved/applied
        if (isAuthenticated && userType === 'expert') {
          const saved = await getSavedJobs();
          setIsSaved(saved.some((s: SavedJob) => s.job_id === data.id));
          // TODO: Check if applied (would need an endpoint or local state)
        }
      } catch (err: any) {
        console.error('Failed to load job:', err);
        if (err.response?.status === 404) {
          setError('Job not found');
        } else {
          setError('Failed to load job details. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    loadJob();
  }, [id, isAuthenticated, userType]);

  const handleSave = async () => {
    if (!job) return;

    try {
      if (isSaved) {
        await unsaveJob(job.id);
        setIsSaved(false);
      } else {
        await saveJob(job.id);
        setIsSaved(true);
      }
    } catch (err) {
      console.error('Failed to save/unsave job:', err);
    }
  };

  const handleApply = () => {
    if (!job) return;
    navigate(`/jobs/${job.slug || job.id}/apply`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-gray-200 rounded-lg" />
                <div className="flex-1">
                  <div className="h-7 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-5 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
              <div className="flex gap-2 mb-6">
                <div className="h-8 bg-gray-200 rounded w-24" />
                <div className="h-8 bg-gray-200 rounded w-28" />
                <div className="h-8 bg-gray-200 rounded w-20" />
              </div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-3/4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
            <p className="text-gray-600 mb-6">
              The job you're looking for might have been removed or is no longer available.
            </p>
            <Link
              to="/jobs"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700"
            >
              Browse All Jobs
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  const isOwnJob = isAuthenticated && userType === 'company' && job.company_id === userId;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li>
              <Link to="/jobs" className="hover:text-gray-700">
                Jobs
              </Link>
            </li>
            <li>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </li>
            <li>
              <Link to={`/jobs?category=${job.category}`} className="hover:text-gray-700">
                {job.category}
              </Link>
            </li>
            <li>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </li>
            <li className="text-gray-900 font-medium truncate">{job.title}</li>
          </ol>
        </nav>

        {/* Job Detail Component */}
        <JobDetail
          job={job}
          onApply={handleApply}
          onSave={isAuthenticated && userType === 'expert' ? handleSave : undefined}
          isSaved={isSaved}
          isApplied={isApplied}
          isOwnJob={isOwnJob}
          isLoggedIn={isAuthenticated}
        />

        {/* Company jobs section */}
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            More jobs from {job.company_name}
          </h3>
          <Link
            to={`/jobs?company=${job.company_id}`}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            View all jobs from this company
          </Link>
        </div>

        {/* Similar jobs section */}
        <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Similar jobs</h3>
          <Link
            to={`/jobs?category=${job.category}&experience_level=${job.experience_level}`}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Browse similar {job.experience_level} {job.category} roles
          </Link>
        </div>
      </div>
    </div>
  );
}
